import React, { useState, useEffect } from 'react';
import { User, RefreshCw, Loader } from 'lucide-react';
import { usersService } from '../../services/users';
import { organizationsService } from '../../services/organizations';
import { User as UserType, Organization } from '../../types';

export const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserType[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const orgs = await organizationsService.getAll();
      setOrganizations(orgs);
    } catch (err) {
      console.error('Erreur lors du chargement des organisations:', err);
    }
    fetchUsers();
  };

  const getOrganizationName = (orgId: string | null | undefined): string => {
    if (!orgId) return '-';
    const org = organizations.find(o => o.id === orgId);
    return org?.name || '-';
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setRefreshing(true);
      const data = await usersService.getAll();
      setUsers(data);
      setError(null);
    } catch (err: any) {
      console.error('Erreur lors du chargement des utilisateurs:', err);
      setError(err.message || 'Une erreur est survenue lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const roleStyles: Record<string, string> = {
    super_admin: 'bg-lavender-container text-lavender rounded-full px-3 py-1 text-xs font-medium',
    org_admin: 'bg-lavender-container text-lavender rounded-full px-3 py-1 text-xs font-medium',
    trainer: 'bg-mint-container text-mint rounded-full px-3 py-1 text-xs font-medium',
    learner: 'bg-cream-high text-ink-soft rounded-full px-3 py-1 text-xs font-medium',
  };

  const roleLabels: Record<string, string> = {
    super_admin: 'Super Admin',
    org_admin: 'Admin Org',
    trainer: 'Formateur',
    learner: 'Apprenant',
  };

  return (
    <div className="bg-white shadow-ambient rounded-2xl p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-ink">Utilisateurs</h2>
          <button
            onClick={fetchUsers}
            disabled={refreshing}
            className="p-1 text-ink-soft hover:text-coral disabled:opacity-50"
            title="Rafraîchir la liste"
          >
            {refreshing ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              <RefreshCw className="w-5 h-5" />
            )}
          </button>
        </div>
        <span className="text-sm text-ink-soft">
          {users.length} utilisateur{users.length > 1 ? 's' : ''}
        </span>
      </div>

      {error && (
        <div className="bg-primary-container text-coral rounded-xl px-4 py-3 text-sm mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-14 px-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gold-container/50 flex items-center justify-center">
            <User size={32} className="text-gold" />
          </div>
          <h3 className="text-lg font-semibold text-ink mb-1">
            Aucun utilisateur trouvé
          </h3>
          <p className="text-sm text-ink-soft max-w-sm mx-auto">
            La gestion des utilisateurs se fait depuis le LMS.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-cream-high">
                <th className="px-6 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-[0.05em]">Email</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-[0.05em]">Role</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-[0.05em]">Organisation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-high">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-cream/50">
                  <td className="px-6 py-4 font-medium text-ink">
                    {user.email}
                  </td>
                  <td className="px-6 py-4">
                    <span className={roleStyles[user.role] || roleStyles.learner}>
                      {roleLabels[user.role] || user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-ink-soft">
                    {getOrganizationName(user.organizationId)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
